import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { useNotificationSocket } from '../context/NotificationSocketContext.jsx';
import { formatDateTime } from '../utils/formatDate.js';

export function AdminNotificationsPage() {
  const [searchParams] = useSearchParams();
  const unreadOnly = searchParams.get('unread') === '1';
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const { subscribe } = useNotificationSocket();

  const load = () =>
    apiJson('/api/notifications')
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    return subscribe((notification) => {
      if (!notification?.id) return;
      setItems((prev) => {
        if (prev.some((n) => n.id === notification.id)) return prev;
        return [notification, ...prev];
      });
    });
  }, [subscribe]);

  const markAll = async () => {
    await apiJson('/api/notifications/read-all', { method: 'POST' });
    await load();
  };

  const markOne = async (id) => {
    await apiJson(`/api/notifications/${id}/read`, { method: 'PUT' });
    await load();
  };

  const visibleItems = unreadOnly ? items.filter((n) => !n.read_at) : items;

  return (
    <div>
      <h2 style={{ marginTop: 0 }} data-tour="page-title">Notifications</h2>
      {unreadOnly ? <p className="muted small">Showing unread alerts only.</p> : null}
      <p className="row-actions">
        <button type="button" className="btn-secondary" onClick={() => void markAll()}>
          Mark all read
        </button>
        <Link to="/admin/profile" className="btn-link">
          Notification preferences
        </Link>
      </p>
      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}
      <ul className="list-plain" data-tour="notifications-list">
        {visibleItems.map((n) => (
          <li
            key={n.id}
            className="auth-card flat"
            style={{ marginBottom: '0.5rem', opacity: n.read_at ? 0.7 : 1 }}
          >
            <div className="row-actions" style={{ justifyContent: 'space-between' }}>
              <div>
                <strong>{n.title}</strong>
                <span className="muted small"> · {n.type}</span>
                {n.body ? <div className="muted small">{n.body}</div> : null}
                <div className="muted small">{formatDateTime(n.created_at)}</div>
                {n.link ? (
                  <Link to={n.link.startsWith('/') ? n.link : '/admin'} className="small">
                    Open →
                  </Link>
                ) : null}
              </div>
              {!n.read_at ? (
                <button type="button" className="btn-link" onClick={() => void markOne(n.id)}>
                  Mark read
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {!loading && !visibleItems.length ? <p className="muted">No notifications.</p> : null}
    </div>
  );
}
